import { useState } from 'react'

import { useReservationModal } from '@/pages/panel-interno/components/ReservationModalContext'
import { getDocument, setDocument } from '@/shared/firebase/DocumentsDAO'
import { cn } from '@/shared/lib/styles/class-merge'
import { useToast } from '@/shared/ui/use-toast'
import { HairSalonScheduledTask, Reservation } from '@monorepo/functions/src/types/reservations'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'

import { useCalendarStore } from '../model/store'
import { findUnscheduledTasks, generateId, isTaskUnscheduled } from '../model/task-utils'
import { TaskCard } from './TaskCard'
import { TaskEditionModal } from './TaskEditionModal'
import { sendHairSalonConfirmationEmail } from '@/shared/api/reservationsAPI'

interface TimeSlotProps {
    time: string
    date: string
    isAvailable?: boolean
    isWeekView?: boolean
    showHour?: boolean
}

export function TimeSlot({ time, date, isAvailable = true, isWeekView = false, showHour = false }: TimeSlotProps) {
    const { scheduledTasks, selectedTask, setSelectedTask, setScheduledTasks } = useCalendarStore()
    const [isTimeSelectionModalOpen, setIsTimeSelectionModalOpen] = useState(false)
    const { toast } = useToast()
    const { openReservationModal } = useReservationModal()

    // Normalize time format to HH:mm
    const normalizeTime = (timeStr: string) => {
        // If time is in HH:mm format, return as is
        if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr

        // If time is in H:mm format, pad with leading zero
        if (/^\d{1}:\d{2}$/.test(timeStr)) return `0${timeStr}`

        // If time is in military format (e.g. "900" for 9:00), convert to HH:mm
        if (/^\d{3,4}$/.test(timeStr)) {
            const hours = timeStr.length === 3 ? timeStr[0] : timeStr.slice(0, 2)
            const minutes = timeStr.length === 3 ? timeStr.slice(1) : timeStr.slice(2)
            return `${hours.padStart(2, '0')}:${minutes}`
        }

        return timeStr
    }

    const tasksStartingInSlot = scheduledTasks.filter(t => {
        if (t.date !== date) return false

        // Get hour and minutes from both times
        const [slotHour] = normalizeTime(time).split(':').map(Number)
        const [taskHour] = normalizeTime(t.time).split(':').map(Number)

        // Match if the hour is the same
        return slotHour === taskHour
    })

    const isTaskCollidingWithPreviousTask = (task: HairSalonScheduledTask) => {
        const taskStartTime = new Date(`${task.date}T${normalizeTime(task.time)}`)
        const taskEndTime = new Date(taskStartTime.getTime() + task.duration * 60000)
        const taskIndex = scheduledTasks.findIndex(t => t.id === task.id)

        return scheduledTasks.some((t, idx) => {
            if (t.id === task.id || idx < taskIndex) return false
            const otherStartTime = new Date(`${t.date}T${normalizeTime(t.time)}`)
            const otherEndTime = new Date(otherStartTime.getTime() + t.duration * 60 * 1000)

            return (
                (taskStartTime >= otherStartTime && taskStartTime < otherEndTime) || // Task starts during another task
                (taskEndTime > otherStartTime && taskEndTime <= otherEndTime) || // Task ends during another task
                (taskStartTime <= otherStartTime && taskEndTime >= otherEndTime) // Task completely overlaps another task
            )
        })
    }

    const handleTaskClick = (task: HairSalonScheduledTask) => {
        if (
            selectedTask &&
            !isTaskUnscheduled(selectedTask) &&
            !isTaskUnscheduled(task) &&
            selectedTask.id === task.id
        ) {
            setSelectedTask(null as any)
            return
        }
        setSelectedTask(task)
    }

    const handleTimeSlotClick = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (selectedTask && isAvailable) {
            const [hour] = normalizeTime(time).split(':')
            setIsTimeSelectionModalOpen(true)
        }
    }

    const handleTaskSave = async (hourWithMinutes: string, service: HairdressingServiceType, duration: number) => {
        if (!selectedTask) return

        try {
            if (isTaskUnscheduled(selectedTask)) {
                // Unscheduled: create a new scheduled task
                const unscheduledTask = selectedTask
                const newTask: HairSalonScheduledTask = {
                    id: generateId(),
                    reservationId: unscheduledTask.reservation.id,
                    date,
                    time: hourWithMinutes,
                    service,
                    duration,
                    petIndex: unscheduledTask.petIndex,
                }
                const calendarScheduledTasks = [...scheduledTasks, newTask]
                setScheduledTasks(calendarScheduledTasks)
                toast({
                    title: 'Tarea creada',
                    description: 'La tarea se ha creado correctamente.',
                })
                setSelectedTask(null as any)
                // Update reservation in Firestore
                const reservation: Reservation | undefined = await getDocument({
                    collectionName: 'reservations',
                    id: unscheduledTask.reservation.id,
                })
                if (reservation) {
                    const updatedTasks = [...(reservation.tasks || []), newTask]
                    const hasUnscheduledTasks = findUnscheduledTasks([{ ...reservation, tasks: updatedTasks }]).length > 0
                    const status = hasUnscheduledTasks ? 'pending' : 'confirmed'

                    console.log('saving reservation from slot with status', status)
                    
                    // If all tasks are scheduled, call the confirmation function
                    if (reservation.type === 'peluqueria' && reservation.status === 'pending' && status === 'confirmed') {
                        console.log('sending confirmation email from slot')
                        sendHairSalonConfirmationEmail({ reservationId: reservation!.id })
                    }
                    
                    await setDocument({
                        collectionName: 'reservations',
                        id: unscheduledTask.reservation.id,
                        data: {
                            ...reservation,
                            ...(reservation.type === 'hotel' ? { hairSalonReservationStatus: status } : { status }),
                            tasks: updatedTasks,
                        },
                    })
                }
            } else {
                // Scheduled: update the existing task
                const newTask: HairSalonScheduledTask = {
                    ...selectedTask,
                    date,
                    time: hourWithMinutes,
                    service,
                    duration,
                }
                const updatedTasks = scheduledTasks.map(t => (t.id === newTask.id ? newTask : t))
                setScheduledTasks(updatedTasks)
                toast({
                    title: 'Tarea actualizada',
                    description: 'La tarea se ha actualizado correctamente.',
                })
                setSelectedTask(null as any)
                // Update reservation in Firestore
                const reservation = (await getDocument<Reservation>({
                    collectionName: 'reservations',
                    id: newTask.reservationId,
                }))!
                const updatedReservationTasks = reservation.tasks!.map(t => (t.id === newTask.id ? newTask : t))
                const status =
                    findUnscheduledTasks([{ ...reservation, tasks: updatedReservationTasks }]).length > 0
                        ? 'pending'
                        : 'confirmed'

                await setDocument({
                    collectionName: 'reservations',
                    id: newTask.reservationId,
                    data: {
                        ...reservation,
                        tasks: updatedReservationTasks,
                        ...(reservation.type === 'hotel' ? { hairSalonReservationStatus: status } : { status }),
                    },
                })
            }
        } catch (error) {
            console.error('Error al crear/actualizar la tarea:', error)
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'No se pudo crear/actualizar la tarea.',
                variant: 'destructive',
            })
        } finally {
            setIsTimeSelectionModalOpen(false)
        }
    }

    const handleTaskDelete = async () => {
        if (!selectedTask || isTaskUnscheduled(selectedTask)) return
        const scheduledTask = selectedTask as HairSalonScheduledTask
        // Remove from store
        const updatedTasks = scheduledTasks.filter(t => t.id !== scheduledTask.id)
        setScheduledTasks(updatedTasks)
        setSelectedTask(null as any)
        // Remove from Firestore
        const reservation = await getDocument<Reservation>({
            collectionName: 'reservations',
            id: scheduledTask.reservationId,
        })
        if (reservation) {
            const updatedReservation = { ...reservation, tasks: (reservation.tasks || []).filter(t => t.id !== scheduledTask.id) }
            const status = findUnscheduledTasks([updatedReservation]).length > 0 ? 'pending' : 'confirmed'
            if (updatedReservation.type === 'peluqueria') {
                updatedReservation.status = status
            } else if (updatedReservation.type === 'hotel') {
                updatedReservation.hairSalonReservationStatus = status
            }
            console.log('handleTaskDelete', updatedReservation)

            await setDocument({
                collectionName: 'reservations',
                id: scheduledTask.reservationId,
                data: updatedReservation,
            })
        }
        setIsTimeSelectionModalOpen(false)
    }

    const handleGoToReservation = async () => {
        if (!selectedTask || isTaskUnscheduled(selectedTask)) return
        const scheduledTask = selectedTask
        const reservation = await getDocument<Reservation>({
            collectionName: 'reservations',
            id: scheduledTask.reservationId,
        })
        if (reservation) {
            openReservationModal(reservation)
        }
    }

    return (
        <>
            <div
                onClick={handleTimeSlotClick}
                className={cn(
                    'relative min-h-[6rem] border-b border-r transition-colors',
                    showHour && 'pl-12',
                    !isAvailable && 'bg-gray-50',
                    selectedTask && isAvailable && 'cursor-pointer hover:bg-blue-50',
                )}
            >
                {showHour && <div className='absolute left-2 top-2 text-xs text-gray-400'>{time}</div>}

                <div className='relative h-full w-full'>
                    {tasksStartingInSlot.map(task => {
                        const minutes = parseInt(normalizeTime(task.time).split(':')[1]) || 0
                        const offsetPercentage = minutes / 60
                        const isColliding = isTaskCollidingWithPreviousTask(task)

                        return (
                            <div
                                key={task.id}
                                onClick={e => {
                                    e.stopPropagation()
                                    handleTaskClick(task)
                                }}
                                className={cn(
                                    'absolute',
                                    isColliding ? 'left-1/2' : 'left-0',
                                    'w-1/2', // cada task ocupa el 50%
                                    selectedTask &&
                                        !isTaskUnscheduled(selectedTask) &&
                                        selectedTask?.id === task.id &&
                                        'ring-2 ring-blue-500',
                                )}
                                style={{
                                    height: `${(task.duration / 60) * 100}%`,
                                    top: `${offsetPercentage * 6}rem`,
                                    zIndex: minutes > 0 ? 20 : task.duration > 60 ? 10 : 'auto',
                                }}
                            >
                                <TaskCard
                                    task={task}
                                    className='h-full'
                                    showDetails={!isWeekView}
                                    isWeekView={isWeekView}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>

            {selectedTask && (
                <TaskEditionModal
                    isOpen={isTimeSelectionModalOpen}
                    onClose={() => setIsTimeSelectionModalOpen(false)}
                    onSave={handleTaskSave}
                    hour={normalizeTime(time).split(':')[0]}
                    task={selectedTask}
                    {...(!isTaskUnscheduled(selectedTask)
                        ? { onDelete: handleTaskDelete, onGoToReservation: handleGoToReservation }
                        : { onDelete: () => {} })}
                />
            )}
        </>
    )
}
